import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  MoreVert,
  ShoppingCart,
  AttachMoney,
  Restaurant,
  PersonAdd,
  ArrowForward,
  SmartToy as Brain,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import DashboardContext from '../contexts/DashboardContext';
import apiClient from '../../../api/client';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const MotionDiv = motion.div;

// StatCard component for dashboard statistics
const StatCard = ({ title, value, subtitle, icon, color, percentChange }) => {
  const theme = useTheme();
  return (
    <MotionDiv whileHover={{ y: -5 }} transition={{ duration: 0.3 }}>
      <Card sx={{ height: '100%', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, my: 1 }}>
                {value}
              </Typography>
            </Box>
            <Avatar
              sx={{
                bgcolor: `${color}.lighter`,
                color: `${color}.main`,
                width: 48,
                height: 48,
              }}
            >
              {icon}
            </Avatar>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {percentChange > 0 ? (
              <Chip
                icon={<TrendingUp fontSize="small" />}
                label={`+${percentChange}%`}
                size="small"
                sx={{
                  bgcolor: theme.palette.success.light + '20',
                  color: theme.palette.success.main,
                  fontWeight: 600,
                  mr: 1,
                }}
              />
            ) : (
              <Chip
                icon={<TrendingDown fontSize="small" />}
                label={`${percentChange}%`}
                size="small"
                sx={{
                  bgcolor: theme.palette.error.light + '20',
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  mr: 1,
                }}
              />
            )}
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </MotionDiv>
  );
};

const Overview = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const hasLoadedRef = useRef(false);


  // Context & States
  const { userInfo } = useContext(DashboardContext);
  const [greeting, setGreeting] = useState('');
  const [orderSummary, setOrderSummary] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [aiInsights, setAIInsights] = useState([]);
  const [salesData, setSalesData] = useState([]);

  const { refreshTrigger } = useContext(DashboardContext);

  // Format date function wrapped in useCallback to prevent recreation on every render
  const formatDate = useCallback((dateString) => {
    if (!dateString) return t('common.notAvailable');
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy h:mm a');
    } catch {
      // Empty catch block without parameter
      return dateString;
    }
  }, [t]);

  // API Calls
  const fetchOrderSummary = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/v1/report/dashboard-order-summary');
      setOrderSummary(res.data);
    } catch (error) {
      console.error('Error fetching order summary:', error);
    }
  }, []);

  const fetchRecentTransactions = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/v1/report/dashboard-recent-transaction');
      const formatted = res.data.map((transaction) => ({
        id: `ORD-${transaction.order_number}`,
        items: transaction.qty,
        total: parseFloat(transaction.amount),
        status: t('dashboard.completed'),
        date: formatDate(transaction.order_date),
      }));
      setRecentTransactions(formatted);
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
    }
  }, [t, formatDate]);

  // Helper function to manually replace interpolation if i18n fails
  const interpolateMessage = useCallback((key, params) => {
    let message = t(key);
    
    // Check if interpolation worked
    if (message.includes('{') && message.includes('}')) {
      // Manual string replacement if i18n interpolation failed
      Object.keys(params).forEach(paramKey => {
        const placeholder = `{${paramKey}}`;
        message = message.replace(new RegExp(placeholder, 'g'), params[paramKey]);
      });
    }
    
    return message;
  }, [t]);

  const fetchAIInsights = useCallback(async () => {
    try {
      const [peakRes, pairRes, milestoneRes] = await Promise.all([
        apiClient.get('/api/v1/report/dashboard-peak-info'),
        apiClient.get('/api/v1/report/dashboard-frequently-bought-together'),
        apiClient.get('/api/v1/report/dashboard-target-milestone'),
      ]);

      console.log('AI Insights API Response:', {
        peak: peakRes.data,
        pair: pairRes.data,
        milestone: milestoneRes.data
      });

      const insights = [];

      // Milestone Insight
      const milestoneData = milestoneRes.data?.[0];
      if (milestoneData && milestoneData.orders_to_milestone !== undefined && milestoneData.orders_to_milestone !== 100) {
        const ordersLeft = Number(milestoneData.orders_to_milestone) || 0;
        const milestone = milestoneData.nearest_milestone || 'target';
        
        console.log('Milestone data:', { ordersLeft, milestone });
        
        // Test both interpolation methods
        const interpolatedMessage = interpolateMessage('dashboard.milestoneInsight', {
          ordersLeft: ordersLeft,
          milestone: milestone
        });
        
        console.log('Milestone message result:', interpolatedMessage);
        
        insights.push({
          message: interpolatedMessage,
          priority: 'info'
        });
      }

      // Peak Time Insight
      const peakData = peakRes.data?.[0];
      if (peakData && peakData.time_range && peakData.avg_total_orders !== undefined) {
        const timeRange = peakData.time_range || 'Unknown';
        const orderCount = Number(peakData.avg_total_orders) || 0;
        
        console.log('Peak data:', { timeRange, orderCount });
        
        const interpolatedMessage = interpolateMessage('dashboard.peakTimeInsight', {
          timeRange: timeRange,
          orderCount: orderCount
        });
        
        console.log('Peak message result:', interpolatedMessage);
        
        insights.push({
          message: interpolatedMessage,
          priority: 'high'
        });
      }

      // Top Pair Insight
      const pairData = pairRes.data?.[0];
      if (pairData && pairData.item_1_name && pairData.item_2_name && pairData.pair_count !== undefined) {
        const item1 = pairData.item_1_name || 'Item 1';
        const item2 = pairData.item_2_name || 'Item 2';
        const count = Number(pairData.pair_count) || 0;
        
        console.log('Pair data:', { item1, item2, count });
        
        const interpolatedMessage = interpolateMessage('dashboard.topPairInsight', {
          item1: item1,
          item2: item2,
          count: count
        });
        
        console.log('Pair message result:', interpolatedMessage);
        
        insights.push({
          message: interpolatedMessage,
          priority: 'medium'
        });
      }

      console.log('Generated insights:', insights);
      setAIInsights(insights);
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      // Set fallback insights for testing with real data
      const fallbackInsights = [
        {
          message: interpolateMessage('dashboard.milestoneInsight', { 
            ordersLeft: 15,
            milestone: 100
          }),
          priority: 'info'
        },
        {
          message: interpolateMessage('dashboard.peakTimeInsight', { 
            timeRange: '12:00-14:00',
            orderCount: 25
          }),
          priority: 'high'
        },
        {
          message: interpolateMessage('dashboard.topPairInsight', { 
            item1: 'Nasi Lemak',
            item2: 'Teh Tarik',
            count: 8
          }),
          priority: 'medium'
        }
      ];
      console.log('Using fallback insights:', fallbackInsights);
      setAIInsights(fallbackInsights);
    }
  }, [interpolateMessage]);

  const fetchSalesGraph = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/v1/report/report-sales-graph', {
        params: {filter_type: 'daily'},
      });
      const formatted = res.data.map(item => ({
        name: item.time_group,
        sales: item.total_sales
      }));
      setSalesData(formatted);
    } catch (error) {
      console.error('Error fetching sales graph data:', error);
    }
  }, []);

   // Greeting logic
  useEffect(() => {
    const currentHour = new Date().getHours();
    let greetingText = '';
    if (currentHour < 12) greetingText = t('dashboard.goodMorning');
    else if (currentHour < 18) greetingText = t('dashboard.goodAfternoon');
    else greetingText = t('dashboard.goodEvening');
    setGreeting(greetingText);
  }, [t]);

  // Fetch all data
  useEffect(() => {
    // Prevent duplicate calls
    if (hasLoadedRef.current) {
      console.log('⚠️ Overview: Data already loaded, skipping...');
      return;
    }
    
    hasLoadedRef.current = true;
    
    console.log('✅ Overview: Loading data for the first time');
    fetchOrderSummary();
    fetchRecentTransactions();
    fetchAIInsights();
    fetchSalesGraph();
  }, [refreshTrigger, fetchOrderSummary, fetchRecentTransactions, fetchAIInsights, fetchSalesGraph]);

  return (
    <Box>
      {/* Header */}
      <MotionDiv initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {greeting}, {userInfo.userName}!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {t('dashboard.subtitle')}
          </Typography>
        </Box>
      </MotionDiv>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MotionDiv initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.lighter', color: 'primary.main', mr: 2 }}>
                    <AttachMoney />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('dashboard.totalSales')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  ${parseFloat(orderSummary[0]?.total_revenue || 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionDiv initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.lighter', color: 'info.main', mr: 2 }}>
                    <ShoppingCart />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('dashboard.totalOrders')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {orderSummary[0]?.total_orders || 0}
                </Typography>
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionDiv initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.lighter', color: 'success.main', mr: 2 }}>
                    <Restaurant />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('dashboard.avgOrderValue')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  ${parseFloat(orderSummary[0]?.avg_order_value || 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionDiv initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.lighter', color: 'warning.main', mr: 2 }}>
                    <PersonAdd />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('dashboard.avgItemQty')}
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {parseFloat(orderSummary[0]?.avg_items_per_order || 0).toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <MotionDiv initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
            <Card sx={{ height: 440 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('dashboard.salesOverview')}</Typography>
                  <IconButton size="small"><MoreVert fontSize="small" /></IconButton>
                </Box>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={salesData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis 
                        dataKey="name" 
                        tickFormatter={(tick) => {
                          const date = new Date(tick);
                          if (!isNaN(date.getTime())) {
                            return new Date(tick).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric'
                            });
                          }
                          return tick;
                        }}
                      />
                      <YAxis />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 12,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          border: 'none',
                        }}
                        formatter={(value) => [`$${value}`, t('dashboard.sales')]}
                        labelFormatter={(label) => {
                          const date = new Date(label);
                          if (!isNaN(date.getTime())) {
                            return new Date(label).toLocaleString();
                          }
                          return label;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="sales"
                        stroke="url(#colorGradient)"
                        strokeWidth={3}
                        dot={{ r: 6, strokeWidth: 0, fill: '#667eea' }}
                        activeDot={{ r: 8, strokeWidth: 0, fill: '#764ba2' }}
                      />
                      <defs>
                        <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="#667eea" />
                          <stop offset="100%" stopColor="#764ba2" />
                        </linearGradient>
                      </defs>
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>
        <Grid item xs={12} md={4}>
          <MotionDiv initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: 0.2 }}>
            <Card sx={{ height: 440 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('dashboard.aiInsights')}</Typography>
                  <IconButton size="small"><MoreVert fontSize="small" /></IconButton>
                </Box>
                {aiInsights.length === 0 ? (
                  <Typography color="text.secondary">{t('dashboard.noInsights')}</Typography>
                ) : (
                  <List disablePadding>
                    {aiInsights.map((insight, index) => (
                      <React.Fragment key={index}>
                        <ListItem
                          sx={{
                            borderRadius: 2,
                            bgcolor:
                              insight.priority === 'high'
                                ? theme.palette.error.light + '20'
                                : insight.priority === 'medium'
                                ? theme.palette.warning.light + '20'
                                : theme.palette.info.light + '20',
                            mb: 1,
                            p: 2,
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                bgcolor:
                                  insight.priority === 'high'
                                    ? theme.palette.error.main
                                    : insight.priority === 'medium'
                                    ? theme.palette.warning.main
                                    : theme.palette.info.main,
                              }}
                            >
                              <Brain />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {insight.message}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {t('dashboard.priority')}: {t(`dashboard.priority.${insight.priority}`)}
                              </Typography>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>
      </Grid>

      {/* Recent Orders */}
      <MotionDiv initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('dashboard.recentOrders')}</Typography>
              <Button endIcon={<ArrowForward />} sx={{ textTransform: 'none', fontWeight: 500 }} color="primary">
                {t('dashboard.viewAll')}
              </Button>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dashboard.table.orderId')}</TableCell>
                    <TableCell>{t('dashboard.table.items')}</TableCell>
                    <TableCell>{t('dashboard.table.total')}</TableCell>
                    <TableCell>{t('dashboard.table.status')}</TableCell>
                    <TableCell>{t('dashboard.table.dateTime')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTransactions.length > 0
                    ? recentTransactions.map((order) => (
                        <TableRow key={order.id} hover>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{order.id}</Typography></TableCell>
                          <TableCell>{order.items}</TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>${order.total.toFixed(2)}</Typography></TableCell>
                          <TableCell>
                            <Chip label={order.status} size="small" sx={{
                              bgcolor: theme.palette.success.light + '20',
                              color: theme.palette.success.main,
                              fontWeight: 500,
                            }} />
                          </TableCell>
                          <TableCell>{order.date}</TableCell>
                        </TableRow>
                      )
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">{t('dashboard.noRecentOrders')}</TableCell>
                      </TableRow>
                    )
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </MotionDiv>
    </Box>
  );
};

export default Overview;